import { getPublicFeatureAccess } from '@luxuryescapes/contract-svc-promo/feature-access'
import { useQuery } from '@tanstack/react-query'
import { getMyVouchersCount } from 'api/promo'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import { isVoucherHidden } from 'lib/voucher/isVoucherHidden'
import { useActiveVouchersContext } from 'marketing/contexts/ActiveVouchersContext'
import { useMemo } from 'react'

const NAVIGATION_VOUCHERS_COUNT_STALE_TIME_MS = 15 * 60 * 1000
const NAVIGATION_VOUCHERS_COUNT_CACHE_TIME_MS = 30 * 60 * 1000

function useNavigationVouchersCount({ enabled = true } = {}) {
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const authRoles = useAppSelector(state => state.auth.account.roles)
  const canUseCountApi = getPublicFeatureAccess('voucher', {
    brand: config.BRAND,
    region,
    authRoles,
    isTestEnvironment: config.APP_ENV !== 'production',
  }).canUse === true
  const countQuery = useQuery({
    queryKey: ['my-vouchers-navigation-count', config.BRAND, region, memberId],
    queryFn: () => getMyVouchersCount({ brand: config.BRAND, region }),
    enabled: enabled && !!memberId && canUseCountApi,
    staleTime: NAVIGATION_VOUCHERS_COUNT_STALE_TIME_MS,
    cacheTime: NAVIGATION_VOUCHERS_COUNT_CACHE_TIME_MS,
  })
  const { vouchers, isLoading } = useActiveVouchersContext({
    // Fetch the list only when the count API is unavailable or has failed.
    enabled: enabled && !!memberId && (!canUseCountApi || countQuery.isError),
  })
  const previousCount = useMemo(
    () => vouchers.filter(voucher => !isVoucherHidden(voucher)).length,
    [vouchers],
  )

  return {
    count: canUseCountApi ? (countQuery.data ?? previousCount) : previousCount,
    isLoading: canUseCountApi ? countQuery.isInitialLoading : isLoading,
  }
}

export default useNavigationVouchersCount
